import z from 'zod';

import { ReportComponentType } from '@/types';

// ----------------------
// Query Layer
// ----------------------

export const MetricSchema = z.object({
    field: z.string(),
    aggregation: z.enum(['sum', 'avg', 'count', 'min', 'max']),
    label: z.string().optional(),
});

export const DimensionSchema = z.object({
    field: z.string(),
    label: z.string().optional(),
    sort: z.enum(['asc', 'desc']).optional(),
    sortBy: z.enum(['asc', 'desc']).optional(),
});

export const FilterRuleSchema = z.object({
    field: z.string(),
    operator: z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'not_in', 'contains']),
    // value: z.unknown(),
    values: z.array(z.any()).default([]),
});

export const FilterGroupSchema = z.object({
    condition: z.enum(['AND', 'OR']).default('AND'),
    rules: z.array(FilterRuleSchema),
});

const JsonLogicSchema: z.ZodType<unknown> = z.lazy(() =>
    z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.null(),
        z.array(JsonLogicSchema),
        z.record(z.string(), JsonLogicSchema),
    ]),
);

export const FilterExpressionSchema = z.union([JsonLogicSchema, FilterGroupSchema]);

export const QuerySchema = z
    .object({
        datasetId: z.string().optional(),
        filters: FilterExpressionSchema.optional(),
        metrics: z.array(MetricSchema).optional(),
        dimensions: z.array(DimensionSchema).optional(),
        dateRange: z
            .object({
                start: z.string().optional(),
                end: z.string().optional(),
                preset: z.enum(['last_7_days', 'last_30_days', 'ytd', 'custom']).optional(),
            })
            .optional(),
        limit: z.number().int().positive().optional(),
        orderBy: z
            .object({
                field: z.string(),
                direction: z.enum(['asc', 'desc']),
            })
            .optional(),
    })
    .passthrough();

// ----------------------
// Dataset Schema
// ----------------------

export const DataSetSchema = z.object({
    type: z.string(), // later you can enum this
    key: z.string().optional(),
    providerId: z.string().optional(),
});

// ----------------------
// Shared Config Schema
// ----------------------

export const FormattingSchema = z
    .object({
        showLegend: z.boolean().optional(),
        showAxisLabels: z.boolean().optional(),
        showZeroValues: z.boolean().optional(),
        showAverageLine: z.boolean().optional(),
        attribution: z.boolean().optional(),
        numberFormat: z.enum(['number', 'currency', 'percent']).optional(),
        decimals: z.number().int().min(0).max(10).optional(),
        // 🔥 include your UI additions
        layout: z.enum(['horizontal', 'vertical']).optional(),
        groupBy: z.enum(['grouped', 'stacked']).optional(),
    })
    .passthrough(); // ← important for forward compatibility

export const DateRangeSchema = z.object({
    start: z.string().optional(),
    end: z.string().optional(),
    preset: z.enum(['last_7_days', 'last_30_days', 'ytd', 'custom']).optional(),
});

export const OverviewSchema = z.object({
    dataSourceId: z.string().optional(),
    dateRange: DateRangeSchema.optional(),
    filters: z.array(FilterGroupSchema).optional(),
});

export const BaseComponentConfigSchema = z
    .object({
        type: z.enum(ReportComponentType),

        version: z.number().optional(),

        // ✅ NEW CORE MODEL
        dataSet: DataSetSchema.optional(),
        query: QuerySchema.optional(),

        // existing
        overview: OverviewSchema.optional(),
        formatting: FormattingSchema.optional(),

        titleOverride: z.string().optional(),
        descriptionOverride: z.string().optional(),
    })
    .passthrough();

// Types

export const TitleConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.TITLE),
    text: z.string(),
}).passthrough();

export const DescriptionConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.DESCRIPTION),
    body: z.string().max(200_000).optional(),
    markdown: z.string().max(200_000).optional(),
}).passthrough();

export const StatisticConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.STATISTIC),
    label: z.string().optional(),
    value: z.number().optional(),
    prefix: z.string().optional(),
    suffix: z.string().optional(),
}).passthrough();

export const MultipleStatisticsConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.MULTIPLE_STATISTICS),
}).passthrough();

export const MapConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.MAP),
    latitudeField: z.string().optional(),
    longitudeField: z.string().optional(),
    metric: MetricSchema.optional(),
    map: z
        .object({
            zoom: z.number().min(0).max(24).optional(),
            center: z.tuple([z.number(), z.number()]).optional(),
            style: z.enum(['light', 'dark', 'satellite']).optional(),
        })
        .optional(),
    markers: z
        .object({
            sizeByMetric: z.boolean().optional(),
            cluster: z.boolean().optional(),
        })
        .optional(),
}).passthrough();

export const EmbedConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.EMBED),
    url: z.string().url().optional(),
}).passthrough();

export const BarChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.BAR_CHART),

    options: z
        .object({
            horizontal: z.boolean().optional(),
            barRadius: z.number().optional(),
        })
        .optional(),
}).passthrough();

export const LineChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.LINE_CHART),

    options: z
        .object({
            curve: z.enum(['linear', 'monotone', 'step']).optional(),
            showPoints: z.boolean().optional(),
            area: z.boolean().optional(),
        })
        .optional(),
}).passthrough();

export const AreaChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.AREA_CHART),
    options: z
        .object({
            curve: z.enum(['linear', 'monotone', 'step']).optional(),
            stacked: z.boolean().optional(),
        })
        .optional(),
}).passthrough();

export const ScatterPlotConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.SCATTER_PLOT),
    options: z
        .object({
            pointSize: z.number().optional(),
            colorBy: z.string().optional(),
        })
        .optional(),
}).passthrough();

export const PieChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.PIE_CHART),
    options: z
        .object({
            padAngle: z.number().optional(),
            cornerRadius: z.number().optional(),
        })
        .optional(),
}).passthrough();

export const DonutChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.DONUT_CHART),
    options: z
        .object({
            innerRadius: z.number().optional(),
            padAngle: z.number().optional(),
            cornerRadius: z.number().optional(),
        })
        .optional(),
}).passthrough();

export const HeatMapConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.HEAT_MAP),
    options: z
        .object({
            reverseColorScale: z.boolean().optional(),
        })
        .optional(),
}).passthrough();

export const FunnelChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.FUNNEL_CHART),
    options: z
        .object({
            direction: z.enum(['horizontal', 'vertical']).optional(),
            showConversionRate: z.boolean().optional(),
        })
        .optional(),
}).passthrough();

export const SankeyChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.SANKEY_CHART),
    options: z
        .object({
            nodePadding: z.number().optional(),
            nodeWidth: z.number().optional(),
        })
        .optional(),
}).passthrough();

export const RadarChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.RADAR_CHART),
    options: z
        .object({
            levels: z.number().optional(),
            maxValue: z.number().optional(),
        })
        .optional(),
}).passthrough();

export const RadialChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.RADIAL_CHART),
    options: z
        .object({
            innerRadius: z.number().optional(),
            padAngle: z.number().optional(),
        })
        .optional(),
}).passthrough();

export const GaugeChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.GAUGE_CHART),
    options: z
        .object({
            min: z.number().optional(),
            max: z.number().optional(),
        })
        .optional(),
}).passthrough();

export const ChordChartConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.CHORD_CHART),
    options: z
        .object({
            padAngle: z.number().optional(),
            sort: z.enum(['asc', 'desc']).optional(),
        })
        .optional(),
}).passthrough();

// Table

export const TableColumnSchema = z.object({
    field: z.string(),
    label: z.string().optional(),
    width: z.number().optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    format: z.enum(['number', 'currency', 'percent', 'string']).optional(),
    sortable: z.boolean().optional(),
});

export const TableConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.TABLE),

    columns: z.array(TableColumnSchema).optional(), // ← allow empty initially

    pagination: z
        .object({
            pageSize: z.number().optional(),
        })
        .optional(),
}).passthrough();

export const VantageConfigSchema = BaseComponentConfigSchema.extend({
    type: z.literal(ReportComponentType.COUNTERCYCLICAL_VANTAGE),
    sql: z.string().max(50_000).optional(),
    visualization: z.enum(['table', 'bar', 'line', 'pie', 'json']).default('table').optional(),
    displayMode: z.enum(['code', 'codeAndVisualization', 'visualization']).default('visualization').optional(),
    limit: z.number().int().min(1).max(5_000).optional(),
    preferences: z
        .object({
            autocomplete: z.boolean().optional(),
            formatSqlOnRun: z.boolean().optional(),
            resultDensity: z.enum(['comfortable', 'compact']).optional(),
            validateOnChange: z.boolean().optional(),
        })
        .optional(),
    dataBlocks: z
        .array(
            z
                .object({
                    id: z.string(),
                    referenceName: z.string(),
                    sql: z.string().max(50_000),
                    visualization: z.enum(['table', 'bar', 'line', 'pie', 'json']),
                    displayMode: z.enum(['code', 'codeAndVisualization', 'visualization']),
                    limit: z.number().int().min(1).max(5_000),
                    columns: z.array(
                        z.object({
                            name: z.string(),
                            type: z.string(),
                            nullable: z.boolean().optional(),
                        }),
                    ),
                    rowCount: z.number().int().min(0),
                    runtimeMs: z.number().min(0),
                    result: z
                        .object({
                            columns: z.array(
                                z.object({
                                    name: z.string(),
                                    type: z.string(),
                                    nullable: z.boolean().optional(),
                                }),
                            ),
                            rows: z.array(z.record(z.string(), z.unknown())),
                            rowCount: z.number().int().min(0),
                            runtimeMs: z.number().min(0),
                        })
                        .optional(),
                    createdAt: z.string(),
                    updatedAt: z.string(),
                })
                .passthrough(),
        )
        .optional(),
}).passthrough();

// !IMPORTANT: Needs better typing
export const ReportComponentConfigSchemaMap = {
    [ReportComponentType.TITLE]: TitleConfigSchema,
    [ReportComponentType.DESCRIPTION]: DescriptionConfigSchema,
    [ReportComponentType.STATISTIC]: StatisticConfigSchema,
    [ReportComponentType.MULTIPLE_STATISTICS]: MultipleStatisticsConfigSchema,
    [ReportComponentType.MAP]: MapConfigSchema,
    [ReportComponentType.BAR_CHART]: BarChartConfigSchema,
    [ReportComponentType.LINE_CHART]: LineChartConfigSchema,
    [ReportComponentType.AREA_CHART]: AreaChartConfigSchema,
    [ReportComponentType.SCATTER_PLOT]: ScatterPlotConfigSchema,
    [ReportComponentType.PIE_CHART]: PieChartConfigSchema,
    [ReportComponentType.DONUT_CHART]: DonutChartConfigSchema,
    [ReportComponentType.HEAT_MAP]: HeatMapConfigSchema,
    [ReportComponentType.FUNNEL_CHART]: FunnelChartConfigSchema,
    [ReportComponentType.SANKEY_CHART]: SankeyChartConfigSchema,
    [ReportComponentType.RADAR_CHART]: RadarChartConfigSchema,
    [ReportComponentType.RADIAL_CHART]: RadialChartConfigSchema,
    [ReportComponentType.GAUGE_CHART]: GaugeChartConfigSchema,
    [ReportComponentType.CHORD_CHART]: ChordChartConfigSchema,
    [ReportComponentType.TABLE]: TableConfigSchema,
    [ReportComponentType.COUNTERCYCLICAL_VANTAGE]: VantageConfigSchema,
    [ReportComponentType.EMBED]: EmbedConfigSchema,
} as Partial<Record<ReportComponentType, z.ZodTypeAny>>;

export const ReportComponentConfigSchema = z.discriminatedUnion('type', [
    TitleConfigSchema,
    DescriptionConfigSchema,
    StatisticConfigSchema,
    MultipleStatisticsConfigSchema,
    MapConfigSchema,
    BarChartConfigSchema,
    LineChartConfigSchema,
    AreaChartConfigSchema,
    ScatterPlotConfigSchema,
    PieChartConfigSchema,
    DonutChartConfigSchema,
    HeatMapConfigSchema,
    FunnelChartConfigSchema,
    SankeyChartConfigSchema,
    RadarChartConfigSchema,
    RadialChartConfigSchema,
    GaugeChartConfigSchema,
    ChordChartConfigSchema,
    TableConfigSchema,
    VantageConfigSchema,
    EmbedConfigSchema,
]);

export function validateReportComponentConfig(input: any) {
    return ReportComponentConfigSchema.parse(input);
}

export function safeValidateReportComponentConfig(input: any) {
    return ReportComponentConfigSchema.safeParse(input);
}
